import { toast } from '@/components/toast';
import { GenerateAvatarSkeleton } from '@/pages/avatar';
import { DreamsStatus, useCreateDream } from '@/pages/editor/components/hooks/useCreateDream';
import { useDreamsTokens } from '@/pages/editor/components/hooks/useDreamsTokens';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { isImageProcessing } from '@/state/presenters/presenters';
import { AppRoutes } from '@/utils/types';
import styled from '@emotion/styled';
import axios, { AxiosError } from 'axios';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { AddDreamBox } from './AddDreamBox';
import { GenerateButton } from './GenerateButton';

const Prompt = styled.textarea`
    min-height: 180px;
    resize: none;
    border: 1px solid ${({ theme }) => theme.colors.black[300]};
    border-radius: 10px;
    padding: 16px;
    outline: none;
    font-size: 16px;
`;

const UsedTokens = styled.div`
    text-align: right;
    color: ${({ theme }) => theme.colors.secondary[700]};
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: -0.28px;
`;

const DreamContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 12px;

    @media (max-width: 1080px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

export enum ERROR_KINDS {
    CelebrityDetectedError = 'CelebrityDetectedError',
    DreamsInvalidPrompt = 'DreamsInvalidPrompt',
    FaceError = 'FaceError',
    InsufficientCreditsError = 'InsufficientCreditsError',
    InvalidConsentError = 'InvalidConsentError',
    TextModerationError = 'TextModerationError',
    TrainingValidationError = 'TrainingValidationError',
    ExtremePoseDetected = 'ExtremePoseDetected',
    AlgoRequestError = 'AlgoRequestError',
    TooManyRequests = 'TooManyRequests',
}

interface Props {
    from: 'advanced' | 'avatar';
}

export const GenerateAvatar = ({ from }: Props) => {
    const [prompt, setPrompt] = useState('');
    const isLoading = useRecoilValue(isImageProcessing);
    const { data } = useDreamsTokens();
    const mixpanel = useAnalytics();
    const { isAnonymous, loginWithRedirect } = useAuth();
    const { createDream, dreamsResult, setDreamsResult } = useCreateDream();
    const { used, limit } = data!;

    const handleCreateDream = async (prefix: string, prompt: string) => {
        if (isAnonymous) {
            loginWithRedirect({
                appState: { returnTo: window.location.origin + AppRoutes.PostSso + `?from=${from}` },
            });
            return;
        }

        try {
            mixpanel?.track('dreams-generate', { event: 'click' });
            setDreamsResult([]);
            const results = await createDream(prefix, prompt);
            const validResults = results.filter(result => result.status === DreamsStatus.SUCCESS);
            if (validResults.length === 0) {
                const isCelebrityErrorDetected = results.find(result => result.status === DreamsStatus.CELEBRITY_DETECTED);
                if (isCelebrityErrorDetected) {
                    toast.info({
                        title: 'Your prompt includes a celebrity reference.',
                        message: 'Please revise your prompt and try again',
                    });
                } else {
                    toast.info({ title: `Couldn't generate images with faces, please try a different prompt` });
                }
            } else {
                mixpanel?.track('dreams-generate', { event: 'success', prompt, from: from === 'advanced' ? 'studio' : 'avatar' });
            }
            setDreamsResult(validResults);
        } catch (e) {
            if (axios.isAxiosError(e)) {
                const errorType = (e as AxiosError<{ kind: ERROR_KINDS }>)?.response?.data?.kind;

                if (errorType === 'InsufficientCreditsError') {
                    toast.info({ title: 'Credit limit reached', message: 'To generate an avatar you will need to upgrade your plan' });
                    mixpanel?.track('dreams-generate', { event: 'error', prompt, error: errorType });
                } else if (errorType === 'DreamsInvalidPrompt') {
                    toast.error({ title: 'Invalid prompt please try again.' });
                    mixpanel?.track('dreams-generate', { event: 'error', prompt, error: errorType });
                } else if (errorType === 'TooManyRequests') {
                    toast.error({ title: 'Too many requests', message: 'Please try again later' });
                    mixpanel?.track('dreams-generate', { event: 'error', prompt, error: errorType, type: 'too-many-requests' });
                } else {
                    toast.error({ title: 'Something went wrong. Please try again' });
                    mixpanel?.track('dreams-generate', { event: 'error', prompt, error: 'unknown', message: e.message });
                }
                return;
            }

            logger.error(e);
            mixpanel?.track('dreams-generate', { event: 'error', prompt, error: 'unknown' });
        }
    };

    const handleChange: React.ChangeEventHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPrompt(e.currentTarget.value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleCreateDream('A portrait of', prompt);
        }
    };

    return (
        <>
            <Prompt
                disabled={isLoading}
                value={prompt}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="Generate an avatar with short curly hair, green eyes, wearing a blue shirt"
            />
            <GenerateButton
                isLoading={isLoading}
                disabled={prompt.length === 0}
                onClick={() => handleCreateDream('A portrait of', prompt)}
            />

            <UsedTokens>
                {used} / {limit} used
            </UsedTokens>

            {isLoading && !dreamsResult.length && <GenerateAvatarSkeleton />}

            {!!dreamsResult.length && (
                <DreamContainer>
                    {dreamsResult.map(dream => (
                        <AddDreamBox dream={dream} disabled={isLoading} key={dream.id} src={dream.url} />
                    ))}
                </DreamContainer>
            )}
        </>
    );
};
